import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import styled, { css } from 'styled-components'

// Components
import Avatar from './Avatar'
import Icon from './icon/Icon'
import Text from './Text'

// Utils
import { getActivitySubtitle, IActivityItem } from '../utils/activityFeed'
import { useAppDispatch } from '../context/AppContext'

// Styles
import { applyTextStyle } from '../styles/typography'
import { hsla } from '../styles/colors'
import { fastTransition } from '../styles/effects'

// A notification loses its tint once the pointer has rested on it, not merely
// passed over it on the way to the scrollbar.
const DWELL_MS = 400

interface IProps {
  item: IActivityItem
  onNavigate: () => void
  onSeen: (notificationIds: string[]) => void
}

// One row in the activity panel, rendering either a request or a notification from
// the shared IActivityItem shape so a single card serves both. It reports itself as
// seen only after the pointer has rested on it for DWELL_MS, so scrolling past — or
// crossing it on the way to the scrollbar — does not mark it read.
const ActivityItemCard: React.FC<IProps> = ({ item, onNavigate, onSeen }) => {
  const { t } = useTranslation()
  const appDispatch = useAppDispatch()
  const [seen, setSeen] = useState(false)
  const dwellRef = useRef<ReturnType<typeof setTimeout>>()

  const cancelDwell = () => {
    if (dwellRef.current) {
      clearTimeout(dwellRef.current)
    }
  }

  useEffect(() => cancelDwell, [])

  const markSeen = () => {
    if (seen || !item.isUnread) return
    setSeen(true)
    onSeen(item.notificationIds)
  }

  const startDwell = () => {
    if (seen || !item.isUnread) return
    dwellRef.current = setTimeout(markSeen, DWELL_MS)
  }

  const content = (
    <>
      <IconColumn>
        <IconTile $completed={!!item.isCompleted}>
          <Icon type={item.icon} size={16} />
        </IconTile>
        {item.person && (
          <AvatarBadge>
            <Avatar user={item.person} />
          </AvatarBadge>
        )}
      </IconColumn>
      <Body>
        <Title $completed={!!item.isCompleted}>{item.title}</Title>
        <Text textStyle="caption" color="text72" marginTop={2}>
          {getActivitySubtitle(item, t)}
        </Text>
      </Body>
      {(item.url || item.modalRequest) && (
        <Chevron type="ChevronRight" size={14} />
      )}
    </>
  )

  const shared = {
    showTint: item.isUnread && !seen,
    onMouseEnter: startDwell,
    onMouseLeave: cancelDwell,
    onFocus: markSeen,
  }

  if (item.modalRequest) {
    const { modalRequest } = item
    // Opens where the user is: close the panel, then let RequestModals take over
    return (
      <ModalRow
        type="button"
        onClick={() => {
          onNavigate()
          appDispatch({ type: 'updateRequestModal', payload: modalRequest })
        }}
        {...shared}
      >
        {content}
      </ModalRow>
    )
  }

  return item.url ? (
    <ClickableRow
      to={{ pathname: item.url, state: item.linkState }}
      onClick={onNavigate}
      {...shared}
    >
      {content}
    </ClickableRow>
  ) : (
    <StaticRow {...shared}>{content}</StaticRow>
  )
}

export default ActivityItemCard

const Chevron = styled(Icon)`
  flex-shrink: 0;

  svg > path {
    fill: ${({ theme }) => hsla(theme.colors.primary, 0.15)};
    transition: fill ${fastTransition};
  }
`

const rowStyles = css<{ showTint: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  text-decoration: none;
  background-color: transparent;
  transition: background-color ${fastTransition};

  ${({ showTint, theme }) =>
    showTint &&
    css`
      background-color: ${hsla(theme.colors.primary, 0.1)};
    `}
`

const StaticRow = styled.div<{ showTint: boolean }>`
  ${rowStyles};
`

const clickableRowStyles = css<{ showTint: boolean }>`
  ${rowStyles};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background-color: ${({ theme }) => hsla(theme.colors.text, 0.04)};

    ${Chevron} svg > path {
      fill: ${({ theme }) => theme.colors.primary};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.primary};
    outline-offset: -2px;
  }
`

const ClickableRow = styled(Link)<{ showTint: boolean }>`
  ${clickableRowStyles};
`

const ModalRow = styled.button<{ showTint: boolean }>`
  ${clickableRowStyles};
  width: 100%;
  border: none;
  font: inherit;
  text-align: left;
`

const IconColumn = styled.div`
  position: relative;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
`

const IconTile = styled.div<{ $completed: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.textLightest};

  svg > path {
    fill: ${({ theme, $completed }) =>
      $completed ? theme.colors.textLighter : theme.colors.text72};
  }
`

const AvatarBadge = styled.div`
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  box-shadow: 0 0 0 2px ${({ theme }) => theme.colors.lightest};
`

const Body = styled.div`
  min-width: 0;
  flex: 1;
`

const Title = styled.p<{ $completed: boolean }>`
  ${applyTextStyle('label')};
  color: ${({ theme, $completed }) =>
    $completed ? theme.colors.textLighter : theme.colors.text};
  margin: 0;
`
